import Loading from 'mk2/components/Loading';
import React from 'react';
import RLoadable from 'react-loadable';

export type Options<Props, Exports extends object> = OptionsWithoutRender<Props> | OptionsWithRender<Props, Exports>;

export interface CommonOptions {
    /**
     * React component displayed after delay until loader() succeeds. Also responsible for displaying errors.
     *
     * If you don't want to render anything you can pass a function that returns null
     * (this is considered a valid React component).
     */
    // NOTE: () => null is only needed until React.SFC supports components returning null
    loading?: React.ComponentType<RLoadable.LoadingComponentProps> | (() => null);
    /**
     * Defaults to 200, in milliseconds.
     *
     * Only show the loading component if the loader() has taken this long to succeed or error.
     */
    delay?: number | false | null;
    /**
     * Disabled by default.
     *
     * After the specified time in milliseconds passes, the component's `timedOut` prop will be set to true.
     */
    timeout?: number | false | null;

    /**
     * Optional array of module paths that `Loadable.Capture`'s `report` function will be applied on during
     * server-side rendering. This helps the server know which modules were imported/used during SSR.
     * ```ts
     * Loadable({
     *   loader: () => import('./my-component'),
     *   modules: ['./my-component'],
     * });
     * ```
     */
    modules?: string[];

    /**
     * An optional function which returns an array of Webpack module ids which you can get
     * with require.resolveWeak. This is used by the client (inside `Loadable.preloadReady`) to
     * guarantee each webpack module is preloaded before the first client render.
     * ```ts
     * Loadable({
     *  loader: () => import('./Foo'),
     *  webpack: () => [require.resolveWeak('./Foo')],
     * });
     * ```
     */
    webpack?: () => any /* Array<number | string> */;
}

export interface OptionsWithoutRender<Props> extends CommonOptions {
    /**
     * Function returning a promise which returns a React component displayed on success.
     *
     * Resulting React component receives all the props passed to the generated component.
     */
    loader(): Promise<React.ComponentType<Props> | { default: React.ComponentType<Props> }>;
}

export interface OptionsWithRender<Props, Exports extends object> extends CommonOptions {
    /**
     * Function returning a promise which returns an object to be passed to `render` on success.
     */
    loader(): Promise<Exports>;
    /**
     * If you want to customize what gets rendered from your loader you can also pass `render`.
     *
     * Note: If you want to load multiple resources at once, you can also use `Loadable.Map`.
     *
     * ```ts
     * Loadable({
     *     // ...
     *     render(loaded, props) {
     *         const Component = loaded.default;
     *         return <Component {...props} />
     *     }
     * });
     * ```
     */
    render(loaded: Exports, props: Props): React.ReactNode;

    // NOTE: render is not optional if the loader return type is not compatible with the type
    // expected in `OptionsWithoutRender`. If you do not want to provide a render function, ensure that your
    // function is returning a promise for a React.ComponentType or is the result of import()ing a module
    // that has a component as its `default` export.
}

export interface OptionsWithMap<Props, Exports extends { [key: string]: any }> extends CommonOptions {
    /**
     * An object containing functions which return promises, which resolve to an object to be passed to `render` on success.
     */
    loader: {
        [P in keyof Exports]: () => Promise<Exports[P]>
    };
    /**
     * If you want to customize what gets rendered from your loader you can also pass `render`.
     *
     * Note: If you want to load multiple resources at once, you can also use `Loadable.Map`.
     *
     * ```ts
     * Loadable({
     *     // ...
     *     render(loaded, props) {
     *         const Component = loaded.default;
     *         return <Component {...props} />
     *     }
     * });
     * ```
     */
    render(loaded: Exports, props: Props): React.ReactNode;
}

export interface ILoadable {
    <Props, Exports extends object = {}>(options: Options<Props, Exports>): React.ComponentType<Props> & RLoadable.LoadableComponent;

    Capture: React.ComponentType<RLoadable.LoadableCaptureProps>;

    Map<Props, Exports extends { [key: string]: any } = {}>(options: OptionsWithMap<Props, Exports>): React.ComponentType<Props> & RLoadable.LoadableComponent;

    /**
     * This will call all of the LoadableComponent.preload methods recursively until they are all
     * resolved. Allowing you to preload all of your dynamic modules in environments like the server.
     * ```ts
     * Loadable.preloadAll().then(() => {
     *   app.listen(3000, () => {
     *     console.log('Running on http://localhost:3000/');
     *   });
     * });
     * ```
     */
    preloadAll(): Promise<void>;

    /**
     * Check for modules that are already loaded in the browser and call the matching
     * `LoadableComponent.preload` methods.
     * ```ts
     * window.main = () => {
     *   Loadable.preloadReady().then(() => {
     *     ReactDOM.hydrate(
     *       <App/>,
     *       document.getElementById('app'),
     *     );
     *   });
     * };
     * ```
     */
    preloadReady(): Promise<void>;
}

const Loadable: ILoadable = function LoadableFn<Props, Exports extends object = {}>(options: Options<Props, Exports>): React.ComponentType<Props> & RLoadable.LoadableComponent {
    return RLoadable<Props, Exports>({
        loading: Loading,
        delay: 300,
        ...options,
    });
} as ILoadable;

Loadable.Map = function Map<Props, Exports extends { [key: string]: any } = {}>(options: OptionsWithMap<Props, Exports>): React.ComponentType<Props> & RLoadable.LoadableComponent {
    return RLoadable.Map<Props, Exports>({
        loading: Loading,
        delay: 300,
        ...options,
    });
};

export default Loadable;
